import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import { GlassCard } from "../../components/ui/glass-card";
import { PoweredByFooter } from "../../components/PoweredByFooter";
import { apiClient } from "../../services/apiClient";
import { ForgotPasswordRequest } from "../../generated/api-client";

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const request = new ForgotPasswordRequest({ email });
      await apiClient.forgot_password_auth_forgot_password_post(request);
      setSuccess(true);
    } catch {
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        position: "relative",
        overflow: "auto",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <GlassCard hover={false} className="w-full p-4 md:p-8 relative z-10 w-[28rem]">
        <div className="flex justify-center mb-6">
          <img src="/assets/long_light.png" alt="logo" className="h-12 rounded-lg" />
        </div>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Password Dimenticata
        </Typography>

        <Typography align="center" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: "0.875rem" }}>
          Inserisci la tua email e ti invieremo un link per resettare la password.
        </Typography>

        {success ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="success" sx={{ bgcolor: `${primaryColor}15` }}>
              Se l'email esiste, ti abbiamo inviato un link per resettare la password.
            </Alert>
            <Button
              onClick={() => navigate("/login")}
              variant="contained"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
                "&:hover": { filter: "brightness(0.9)" },
              }}
            >
              Torna al Login
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              id="email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="mario@effia.com"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: "8px" } } }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
                "&:hover": { filter: "brightness(0.9)" },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Invia Link Reset"}
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Link
                component="button"
                type="button"
                onClick={() => navigate("/login")}
                sx={{ color: primaryLightColor, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                Torna al Login
              </Link>
            </Box>
          </Box>
        )}
      </GlassCard>

      <PoweredByFooter absolute />
    </Box>
  );
};

export default ForgotPasswordPage;

